import { collection, doc, getDocs, getFirestore, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { Eye, ShieldCheck, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LmsStudent } from "../../components/course/types";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useToast } from "../../components/ui/use-toast";
import { app } from "../../firebase"; // Adjust the path as necessary  
import { getAuth } from "firebase/auth";

const LmsWaitingList = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const [viewingStudent, setViewingStudent] = useState<any | null>(null);
    const [grantAccessStudent, setGrantAccessStudent] = useState<any | null>(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isGrantOpen, setIsGrantOpen] = useState(false);
    const [waitingListStudents, setWaitingListStudents] = useState<any[]>([]); // State for students  

    const db = getFirestore(app);
    const auth = getAuth(app);

    // Fetch waiting list students from Firestore  
    useEffect(() => {
        const coursesUnsubscribe = onSnapshot(
            collection(db, "AuthUserCredentailsRequest"),
            (querySnapshot) => {
                //   setLoader(true); // Show loader while fetching data
                const fetchedStudents = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                })) as any[];

                // Eliminate duplicates based on email  
                const uniqueStudentsMap = new Map();
                fetchedStudents.forEach((student) => {
                    if (!uniqueStudentsMap.has(student.emailId)) {
                        uniqueStudentsMap.set(student.emailId, student);
                    }
                });

                const uniqueStudents = Array.from(uniqueStudentsMap.values()).filter(x => x.isLMSDone == false && x.isInternal == true);
                setWaitingListStudents(uniqueStudents);
            },
            (error) => {
                //  addToast(error.message, "error"); // Handle errors
                //  setLoader(false); // Hide loader in case of error
            }
        );
        return () => {
            coursesUnsubscribe();
        }
    }, [db]);

    const handleViewDetails = (student: LmsStudent) => {
        setViewingStudent(student);
        setIsDetailOpen(true);
    };

    const handleGrantAccess = (student: LmsStudent) => {
        setGrantAccessStudent(student);
        setIsGrantOpen(true);
    };

    const handleSaveAccess = () => {
        if (!grantAccessStudent) return;

        // Update the student's LMS access  
        const updatedStudent = { ...grantAccessStudent, isLMSDone: true };
        updateAuthRequestDetails(updatedStudent);
        updateStudentRecord(updatedStudent);
        // Remove from waiting list  
        setWaitingListStudents(waitingListStudents.filter(student => student.id !== updatedStudent.id));


        // Close the dialog  
        setIsGrantOpen(false);

        toast({
            title: "LMS Access Granted",
            description: `${updatedStudent.name} now has access to the LMS.`,
        });
    };

    const updateAuthRequestDetails = async (student: any) => {
        await updateDoc(doc(db, "AuthUserCredentailsRequest", student?.id), student);
    }

    const updateStudentRecord = async (student: any) => {
        const emailId = student?.emailId;

        if (!emailId) {
            throw new Error("Email ID is required to update the student.");
        }

        try {
            // Step 1: Fetch the student document by emailId  
            const studentsCollection = collection(db, "students");
            const queryData = query(studentsCollection, where("emailId", "==", emailId));
            const querySnapshot = await getDocs(queryData);

            if (querySnapshot.empty) {
                throw new Error(`No student found with email ID: ${emailId}`);
            }

            const studentDoc = querySnapshot.docs[0]; // Assuming emailId is unique and this gives us the first result  
            const studentId = studentDoc.id;

            // Get the data from the document  
            const studentData = studentDoc.data();

            // Create the studentBody with the student data  
            const studentBody = {
                ...studentData,
                isLMSDone: student?.isLMSDone
            };

            // Step 3: Update the student document using the fetched studentId  
            await updateDoc(doc(db, "students", studentId), studentBody);
        } catch (error) {
            console.error("Error updating student:", error);
            throw error; // Rethrow for further handling if necessary  
        }
    };

    const handleExportList = () => {
        toast({
            title: "List Exported",
            description: "The waiting list has been exported successfully.",
        });
    };

    return (
        <div className="container py-6 space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text -2xl font-bold">LMS Waiting List</h1>
                <Button onClick={handleExportList}><Eye className="mr-2 h-4 w-4" /> Export List</Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Students Waiting for LMS Access</CardTitle>
                    <CardDescription>
                        Review and approve students who need access to the learning management system.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {waitingListStudents.length === 0 ? (
                        <div className="text-center py-10">
                            <ShieldCheck className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                            <p className="text-gray-500 text-lg">No students waiting for LMS access</p>
                            <p className="text-gray-400 text-sm mt-1">All students have been processed</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>ID</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Email</TableHead>
                                    {/* <TableHead>Courses</TableHead> */}
                                    <TableHead>LMS Access</TableHead>
                                    <TableHead>Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {waitingListStudents.map((student) => (
                                    <TableRow key={student.id}>
                                        <TableCell className="font-medium">{student.studentIdNo}</TableCell>
                                        <TableCell>{student.studentsName}</TableCell>
                                        <TableCell>{student.emailId}</TableCell>
                                        {/* <TableCell>{student.coursesAssigned}</TableCell> */}
                                        <TableCell>
                                            {student.isLMSDone ? (
                                                <Badge variant="default" className="bg-green-500">Issued</Badge>
                                            ) : (
                                                <Badge variant="destructive">Not Issued</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex space-x-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleViewDetails(student)}
                                                >
                                                    <Eye className="mr-1 h-4 w-4" />
                                                    View
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleGrantAccess(student)}
                                                >
                                                    <ShieldCheck className="mr-1 h-4 w-4" />
                                                    Grant Access
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* View Student Details Dialog */}
            <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Student Details</DialogTitle>
                        <DialogDescription>
                            Information about {viewingStudent?.studentsName}
                        </DialogDescription>
                    </DialogHeader>
                    {viewingStudent && (
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <span className="font-semibold text-right">ID:</span>
                                <span className="col-span-3">{viewingStudent.studentIdNo}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <span className="font-semibold text-right">Name:</span>
                                <span className="col-span-3">{viewingStudent.studentsName}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <span className="font-semibold text-right">Email:</span>
                                <span className="col-span-3">{viewingStudent.emailId}</span>
                            </div>
                            {/* <div className="grid grid-cols-4 items-center gap-4">
                                <span className="font-semibold text-right">Courses:</span>
                                <span className="col-span-3">{viewingStudent.coursesAssigned}</span>
                            </div> */}
                            <div className="grid grid-cols-4 items-center gap-4">
                                <span className="font-semibold text-right">ID Card:</span>
                                <span className="col-span-3">
                                    {viewingStudent.idCardIssued ? (
                                        <Badge variant="default" className="bg-green-500">Issued</Badge>
                                    ) : (
                                        <Badge variant="destructive">Not Issued</Badge>
                                    )}
                                </span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <span className="font-semibold text-right">LMS Access:</span>
                                <span className="col-span-3">
                                    <Badge variant="destructive">Waiting</Badge>
                                </span>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Grant LMS Access Sheet */}
            <Sheet open={isGrantOpen} onOpenChange={setIsGrantOpen}>
                <SheetContent className="sm:max-w-md">
                    <SheetHeader>
                        <SheetTitle>Grant LMS Access</SheetTitle>
                        <SheetDescription>
                            Provide access to the Learning Management System for {grantAccessStudent?.studentsName}
                        </SheetDescription>
                    </SheetHeader>
                    {grantAccessStudent && (
                        <div className="space-y-6 py-6">
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
                                <p className="text-amber-700 text-sm">
                                    You're about to grant LMS access to this student. This action will:
                                </p>
                                <ul className="list-disc list-inside text-amber-700 text-sm mt-2 ml-2">
                                    <li>Create an LMS account for the student</li>
                                    <li>Send access credentials via email</li>
                                    <li>Remove them from the waiting list</li>
                                </ul>
                            </div>

                            <div className="space-y-4">
                                <div className="grid grid-cols-4 items-center gap-4">
                                    <span className="font-semibold text-right">ID:</span>
                                    <span className="col-span-3">{grantAccessStudent?.studentIdNo}</span>
                                </div>
                                <div className="grid grid-cols-4 items-center gap-4">
                                    <span className="font-semibold text-right">Name:</span>
                                    <span className="col-span-3">{grantAccessStudent?.studentsName}</span>
                                </div>
                                <div className="grid grid-cols-4 items-center gap-4">
                                    <span className="font-semibold text-right">Email:</span>
                                    <span className="col-span-3">{grantAccessStudent?.emailId}</span>
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 pt-4">
                                <Button variant="outline" onClick={() => setIsGrantOpen(false)}>
                                    <X className="mr-1 h-4 w-4" />
                                    Cancel
                                </Button>
                                <Button onClick={handleSaveAccess}>
                                    <ShieldCheck className="mr-1 h-4 w-4" />
                                    Grant Access
                                </Button>
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
};

export default LmsWaitingList;  